import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/admin/_common/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/app/admin/_common/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import { dayjsTz } from "@/app/_utils/dayjs";
import PerformerService, { Performer, PerformerShowRow } from "@/app/_services/PerformerService";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { Drama, X, Mail, Phone, AtSign, Calendar } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";
import type { ReactNode } from "react";

function DetailRow({ icon: Icon, children }: { icon: typeof Mail; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-[13px] text-foreground">
      <Icon className="size-3.5 flex-none text-muted-foreground" />
      <span className="truncate">{children}</span>
    </div>
  );
}

function PerformerDetails({ performer }: { performer: Performer }) {
  return (
    <div className="flex flex-col gap-2 border-b border-border px-4 py-4">
      <DetailRow icon={Mail}>
        {performer.email ? (
          <a href={`mailto:${performer.email}`} className="hover:underline">
            {performer.email}
          </a>
        ) : (
          "—"
        )}
      </DetailRow>
      <DetailRow icon={Phone}>{performer.phone || "—"}</DetailRow>
      <DetailRow icon={AtSign}>{performer.instagram || "—"}</DetailRow>
      <DetailRow icon={Calendar}>Added {formatUtcDate(performer.created_at)}</DetailRow>
    </div>
  );
}

export default function PerformerShowsDialog({
  performer,
  onClose,
}: {
  performer: Performer | null;
  onClose: () => void;
}) {
  const [shows, setShows] = useState<PerformerShowRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!performer) return;
    setIsLoading(true);
    setHasError(false);
    setShows([]);
    PerformerService.getPerformerShowsAsync(performer.id)
      .then((res) => setShows(Array.isArray(res) ? res : []))
      .catch((e) => {
        console.error("Failed to load performer shows", e);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [performer]);

  const isMobile = useIsMobile();
  const title = performer ? `${performer.first_name} ${performer.last_name}` : "";

  const tableBody = isLoading ? (
    <div className="grid gap-2 p-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  ) : hasError ? (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <CuoEmpty icon={Drama} text="Failed to load shows. Try again." />
    </div>
  ) : (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border md:rounded-md md:border md:border-border">
      <Table
        className="min-w-[480px] table-fixed"
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[45%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Show
            </TableHead>
            <TableHead className="h-8 w-[30%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Location
            </TableHead>
            <TableHead className="h-8 w-[25%] text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Date
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={3} className="h-[380px] p-0">
                <CuoEmpty icon={Drama} text="No shows yet" />
              </TableCell>
            </TableRow>
          ) : (
            shows.map((show) => (
            <TableRow
              key={show.id}
              className="cursor-pointer"
              onClick={() => navigate(`/admin/shows/${show.id}/details`)}
            >
              <TableCell className="py-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  <ShowThumbnail
                    src={show.img_url}
                    alt={show.title}
                    style={{ height: 28, width: 28, borderRadius: 6, flexShrink: 0 }}
                  />
                  <span className="truncate text-[13px] text-foreground">{show.title}</span>
                </div>
              </TableCell>
              <TableCell className="truncate py-2 text-[12.5px] text-muted-foreground">
                {show.location_name ?? "—"}
              </TableCell>
              <TableCell className="whitespace-nowrap py-2 text-right text-[12.5px] text-muted-foreground">
                {dayjsTz(show.start).format("MMM D, YYYY")}
              </TableCell>
            </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  const mobileList = isLoading ? (
    <div className="grid gap-2 p-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  ) : hasError ? (
    <CuoEmpty icon={Drama} text="Failed to load shows. Try again." />
  ) : shows.length === 0 ? (
    <CuoEmpty icon={Drama} text="No shows yet" />
  ) : (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4">
      {shows.map((show) => (
        <div
          key={show.id}
          className="flex cursor-pointer items-center gap-3 border-b border-border py-3 last:border-0"
          onClick={() => navigate(`/admin/shows/${show.id}/details`)}
        >
          <ShowThumbnail
            src={show.img_url}
            alt={show.title}
            style={{ height: 40, width: 40, borderRadius: 6, flexShrink: 0 }}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] text-foreground">{show.title}</div>
            <div className="text-[12.5px] text-muted-foreground">
              {dayjsTz(show.start).format("MMM D, YYYY")}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={performer !== null} onOpenChange={(next) => !next && onClose()}>
        <SheetContent
          side="bottom"
          hideClose
          className="flex h-[85vh] max-h-[85vh] flex-col gap-0 overflow-hidden rounded-t-xl bg-white p-0"
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="inline-flex min-w-0 items-center gap-2 text-[13px] font-normal text-foreground">
              <Drama className="size-4 flex-none text-muted-foreground" />
              <span className="truncate">{title}</span>
            </span>
            <div className="flex flex-none items-center gap-3">
              {!isLoading && !hasError && (
                <span className="text-[12.5px] text-muted-foreground">
                  {shows.length} show{shows.length === 1 ? "" : "s"}
                </span>
              )}
              <button
                type="button"
                onClick={onClose}
                className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                aria-label="Close"
              >
                <X className="size-3.5" />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {performer && <PerformerDetails performer={performer} />}
            <div className="px-4 pb-1 pt-4 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
              Shows
            </div>
            {mobileList}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={performer !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="inset-0 left-0 top-0 m-auto flex h-fit max-h-[80vh] w-full max-w-[560px] translate-x-0 translate-y-0 flex-col bg-white">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <Drama className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">Shows this performer has done.</DialogDescription>
        </DialogHeader>

        {performer && <PerformerDetails performer={performer} />}
        <div className="px-1 pb-1 pt-3 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          Shows
        </div>
        <div className="flex h-[420px] flex-col">{tableBody}</div>
      </DialogContent>
    </Dialog>
  );
}
